import { ChangeEvent, ElementType } from "react";
import { Box, Chip, IconButton, Paper, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import {
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { UserView } from "@my-helpers/common";

interface UserSummaryProps {
    user: UserView;
    handleDeleteClick: (user: UserView) => void;
    handleStatusToggle: (event: ChangeEvent<HTMLInputElement>, user: UserView) => void;
}

const UserSummary: ElementType<UserSummaryProps> = ({ 
    user, handleDeleteClick, handleStatusToggle }) => {

    const theme = useTheme();

    const formatDate = (dateString: string): string => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getRoleColor = (role: string): string => {
        switch (role?.toLowerCase()) {
            case 'admin':
                return theme.palette.info.main;
            case 'user':
                return theme.palette.success.main;
            default:
                return theme.palette.success.main;
        }
    };

    const getStatusColor = (verified: boolean): string => {
        switch (verified) {
            case true:
                return theme.palette.success.main;
            case false:
                return theme.palette.error.main;
            default:
                return theme.palette.success.main;
        }
    };

    return (
        <Paper
            key={user.id}
            sx={{
                width: '70%',
                flex: 1,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': {
                    backgroundColor: 'action.hover',
                }
            }}
        >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        title={user.givenName + ' ' + user.familyName || 'Unknown'}
                    >
                        {user.givenName + ' ' + user.familyName || 'Unknown'}
                    </Typography>
                    <Chip
                        label={user.role || 'Unknown'}
                        size="small"
                        variant="filled"
                        sx={{ 
                            ml: 1,
                            color: 'white',
                            backgroundColor: getRoleColor(user.role || '') 
                        }}
                    />
                    <Chip
                        label={user.emailVerified ? 'Verified' : 'Not Verified'}
                        size="small"
                        variant="outlined"
                        sx={{ 
                            ml: 1,
                            borderColor: getStatusColor(user.emailVerified),
                            color: getStatusColor(user.emailVerified),
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {user.email}
                    {user.created_at && ` • Created: ${formatDate(user.created_at)}`}
                    {user.updated_at && ` • Updated: ${formatDate(user.updated_at)}`}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title={user.status === 'active' ? "Deactivate User" : "Activate User"} arrow>
                    <Switch
                        checked={user.status === 'active'}
                        onChange={(e) => handleStatusToggle(e, user)}
                        size="small"
                        color="primary"
                    />
                </Tooltip>
                <Tooltip title="Delete User">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(user);
                        }}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'error.main'
                            }
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
}

export default UserSummary;
